import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Phone, Mail, Building, Clock, AlertTriangle, Truck, Plus, Send, Calendar, ExternalLink, CheckSquare, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DealsPanel from "@/components/admin/DealsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGE_LABELS = { new_lead: "New Lead", contacted: "Contacted", in_negotiation: "Negotiation", will_call_when_needed: "Will Call When Needed", closed_won: "Won", closed_lost: "Lost" };
const STAGE_COLORS = { new_lead: "bg-blue-100 text-blue-800", contacted: "bg-yellow-100 text-yellow-800", in_negotiation: "bg-orange-100 text-orange-800", will_call_when_needed: "bg-purple-100 text-purple-800", closed_won: "bg-green-100 text-green-800", closed_lost: "bg-gray-100 text-gray-500" };
const SOURCE_LABELS = { facebook: "Facebook", cold_call: "Cold Call", referral: "Referral", website: "Website", manual: "Manual" };
const TYPE_COLORS = { call: "bg-green-500", message: "bg-blue-500", email: "bg-purple-500", meeting: "bg-orange-500", appointment: "bg-indigo-500", request: "bg-yellow-500" };

function daysSince(d) { if (!d) return 999; return Math.floor((Date.now() - new Date(d).getTime()) / 86400000); }

export default function CRMClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [interactionType, setInteractionType] = useState("call");
  const [interactionNote, setInteractionNote] = useState("");

  // Task state
  const [tasks, setTasks] = useState([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", task_type: "follow_up", due_date: new Date().toISOString().split("T")[0], priority: "medium" });
  const [savingTask, setSavingTask] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [{ data: d }, { data: taskData }] = await Promise.all([
        axios.get(`${API}/crm/clients/${clientId}`, { withCredentials: true }),
        axios.get(`${API}/tasks?client_id=${clientId}`, { withCredentials: true }),
      ]);
      setData(d);
      setTasks(taskData);
      // Fetch matching forklifts based on linked requests criteria
      if (d.linked_requests?.length > 0) {
        const req = d.linked_requests[0];
        const params = {};
        if (req.condition) params.condition = req.condition;
        if (req.engine_type) params.engine_type = req.engine_type;
        if (req.mast_stages) params.mast_stages = req.mast_stages;
        if (req.tire_type) params.tire_type = req.tire_type;
        if (req.drive) params.drive = req.drive;
        if (req.capacity_min) params.capacity_min = req.capacity_min;
        if (req.capacity_max) params.capacity_max = req.capacity_max;
        if (req.budget_max) params.price_max = req.budget_max;
        const { data: forklifts } = await axios.get(`${API}/forklifts`, { params });
        setMatches(forklifts);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updatePipeline = async (stage) => {
    await axios.patch(`${API}/crm/clients/${clientId}/pipeline`, { pipeline_stage: stage }, { withCredentials: true });
    toast.success(`Stage → ${STAGE_LABELS[stage]}`);
    fetchData();
  };

  const addInteraction = async () => {
    if (!interactionNote.trim()) { toast.error("Add a note for the interaction"); return; }
    await axios.post(`${API}/crm/interactions`, { client_id: clientId, type: interactionType, notes: interactionNote, outcome: "" }, { withCredentials: true });
    toast.success("Interaction logged");
    setInteractionNote("");
    fetchData();
  };

  const updateNotes = async () => {
    if (!noteText.trim()) return;
    const current = data.client.notes || "";
    const timestamp = new Date().toLocaleDateString();
    const updated = `${current}\n[${timestamp}] ${noteText}`.trim();
    // Update both: persistent notes field AND register a CRM interaction
    // so it shows in dashboard as "Last: ..." and resets days_since_contact.
    await axios.put(`${API}/crm/clients/${clientId}`, { notes: updated }, { withCredentials: true });
    try {
      await axios.post(`${API}/crm/interactions`, {
        client_id: clientId,
        type: "note",
        notes: noteText,
        outcome: "",
      }, { withCredentials: true });
    } catch (e) { /* non-blocking */ }
    toast.success("Note added");
    setNoteText("");
    fetchData();
  };

  const createTask = async () => {
    if (!taskForm.title.trim()) { toast.error("Task title required"); return; }
    setSavingTask(true);
    try {
      await axios.post(`${API}/tasks`, { ...taskForm, client_id: clientId, client_name: data?.client?.name || "" }, { withCredentials: true });
      toast.success("Task created");
      setTaskForm({ title: "", task_type: "follow_up", due_date: new Date().toISOString().split("T")[0], priority: "medium" });
      setShowTaskForm(false);
      fetchData();
    } catch { toast.error("Failed to create task"); }
    finally { setSavingTask(false); }
  };

  const toggleTask = async (task) => {
    try {
      const endpoint = task.status === "done" ? "reopen" : "complete";
      await axios.post(`${API}/tasks/${task.id}/${endpoint}`, {}, { withCredentials: true });
      fetchData();
    } catch { toast.error("Failed to update task"); }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`${API}/tasks/${id}`, { withCredentials: true });
      fetchData();
    } catch { toast.error("Failed to delete task"); }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!data) return <div className="p-8 text-center text-gray-500">Client not found</div>;

  const c = data.client;
  const days = daysSince(c.last_contact);
  const req = data.linked_requests?.[0];

  // Build timeline
  const timeline = [];
  (data.interactions || []).forEach(i => timeline.push({ ...i, _type: "interaction", _subtype: i.type, _date: i.created_at }));
  (data.appointments || []).forEach(a => timeline.push({ ...a, _type: "appointment", _subtype: "appointment", _date: a.date || a.created_at }));
  (data.linked_requests || []).forEach(r => timeline.push({ ...r, _type: "request", _subtype: "request", _date: r.created_at }));
  timeline.sort((a, b) => (b._date || "").localeCompare(a._date || ""));

  return (
    <div data-testid="crm-client-detail" className="p-8 max-w-6xl">
      {/* Header */}
      <button onClick={() => navigate("/admin/crm/clients")} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Clients
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">{c.name}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
            {c.company && <span className="flex items-center gap-1"><Building className="w-3.5 h-3.5" />{c.company}</span>}
            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-gray-900"><Phone className="w-3.5 h-3.5" />{c.phone}</a>}
            {c.email && c.email.indexOf("@noemail") === -1 && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-gray-900"><Mail className="w-3.5 h-3.5" />{c.email}</a>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">{SOURCE_LABELS[c.source] || c.source}</Badge>
          <Select value={c.pipeline_stage} onValueChange={updatePipeline}>
            <SelectTrigger className="h-8 w-32 border-none">
              <Badge className={`${STAGE_COLORS[c.pipeline_stage]} border-none text-xs`}>{STAGE_LABELS[c.pipeline_stage]}</Badge>
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Follow-up alert */}
          {days >= 3 && !["closed_won", "closed_lost"].includes(c.pipeline_stage) && (
            <div className={`flex items-center gap-3 p-4 rounded-sm border ${days >= 5 ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"}`}>
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${days >= 5 ? "text-red-500" : "text-orange-500"}`} />
              <div>
                <p className={`text-sm font-semibold ${days >= 5 ? "text-red-800" : "text-orange-800"}`}>
                  {days >= 5 ? "Urgent follow-up needed!" : "Follow-up recommended"}
                </p>
                <p className="text-xs text-gray-600">Last contacted {days} days ago — {c.last_contact ? new Date(c.last_contact).toLocaleDateString() : "never"}</p>
              </div>
            </div>
          )}

          {/* Client needs / Request details */}
          {req && (
            <div className="bg-white border border-gray-200 rounded-sm p-5">
              <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">Client Needs (ForkFinder Request)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Condition", value: req.condition || "Any" },
                  { label: "Engine", value: req.engine_type || "Any" },
                  { label: "Mast", value: req.mast_stages ? `${req.mast_stages} stages` : "Any" },
                  { label: "Tires", value: req.tire_type || "Any" },
                  { label: "Drive", value: req.drive || "Any" },
                  { label: "Capacity", value: req.capacity_min || req.capacity_max ? `${req.capacity_min || "—"} - ${req.capacity_max || "—"} lbs` : "Any" },
                  { label: "Budget", value: req.budget_max ? `$${req.budget_max.toLocaleString()}` : "No limit" },
                  { label: "Status", value: req.status || "active" },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-sm p-2.5">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">{item.label}</p>
                    <p className="text-sm text-gray-900 font-medium capitalize">{item.value}</p>
                  </div>
                ))}
              </div>
              {req.notes && <p className="text-xs text-gray-600 mt-3 bg-yellow-50 border border-yellow-100 rounded-sm p-2">{req.notes}</p>}
            </div>
          )}

          {/* Matching forklifts */}
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">
              Matching Forklifts ({matches.length})
            </h3>
            {matches.length === 0 ? (
              <p className="text-sm text-gray-500">No matching forklifts in inventory right now.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {matches.map((f) => (
                  <div key={f.id} className="flex items-center gap-4 p-3 bg-gray-50 border border-gray-100 rounded-sm hover:bg-gray-100 transition-colors">
                    <div className="w-16 h-12 bg-gray-200 rounded-sm overflow-hidden flex-shrink-0">
                      {f.image_url ? (
                        <img src={f.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${f.image_url}` : f.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Truck className="w-5 h-5 text-gray-400" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{f.brand} {f.model}</p>
                      <p className="text-xs text-gray-500">{f.condition} · {f.engine_type} · {f.capacity_lbs?.toLocaleString()} lbs · {f.tire_type} · {f.year || "—"}</p>
                    </div>
                    <p className="font-mono text-sm font-bold text-gray-900">${f.price?.toLocaleString()}</p>
                    <a href={`/inventory/${f.id}`} target="_blank" rel="noreferrer" className="p-1.5 text-gray-400 hover:text-blue-600"><ExternalLink className="w-4 h-4" /></a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">Notes</h3>
            {c.notes && <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-sm p-3 mb-3 font-sans">{c.notes}</pre>}
            <div className="flex gap-2">
              <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a quick note..." className="bg-white flex-1" onKeyDown={e => e.key === "Enter" && updateNotes()} />
              <button onClick={updateNotes} className="px-3 py-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 rounded-sm text-sm"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Tasks */}
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-sm font-bold text-gray-900">Tasks</h3>
                {tasks.filter(t => t.status === "pending").length > 0 && (
                  <span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded-full">
                    {tasks.filter(t => t.status === "pending").length} pending
                  </span>
                )}
              </div>
              <button
                data-testid="client-add-task-btn"
                onClick={() => setShowTaskForm(v => !v)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 px-2 py-1 rounded-sm hover:bg-gray-50"
              >
                <Plus className="w-3 h-3" /> Add Task
              </button>
            </div>

            {/* Inline task form */}
            {showTaskForm && (
              <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-sm space-y-2">
                <Input
                  data-testid="client-task-title"
                  value={taskForm.title}
                  onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Task title..."
                  className="bg-white text-sm"
                  onKeyDown={e => e.key === "Enter" && createTask()}
                  autoFocus
                />
                <div className="grid grid-cols-3 gap-2">
                  <Select value={taskForm.task_type} onValueChange={v => setTaskForm(f => ({ ...f, task_type: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Call</SelectItem>
                      <SelectItem value="follow_up">Follow-up</SelectItem>
                      <SelectItem value="reminder">Reminder</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={taskForm.priority} onValueChange={v => setTaskForm(f => ({ ...f, priority: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <input
                    type="date"
                    value={taskForm.due_date}
                    onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))}
                    className="h-8 text-xs border border-gray-200 rounded-sm px-2 bg-white"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={createTask} disabled={savingTask} data-testid="client-task-save-btn"
                    className="flex-1 py-1.5 text-xs font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm disabled:opacity-50">
                    {savingTask ? "Saving..." : "Add Task"}
                  </button>
                  <button onClick={() => setShowTaskForm(false)} className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-sm">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Tasks list */}
            {tasks.length === 0 && !showTaskForm ? (
              <p className="text-sm text-gray-400">No tasks yet for this client.</p>
            ) : (
              <div className="space-y-0 divide-y divide-gray-50">
                {[...tasks.filter(t => t.status === "pending"), ...tasks.filter(t => t.status === "done")].map(task => {
                  const PRIORITY_DOT = { high: "bg-red-500", medium: "bg-yellow-400", low: "bg-gray-300" };
                  const TYPE_LABEL = { call: "Call", follow_up: "Follow-up", reminder: "Reminder", other: "Other" };
                  return (
                    <div key={task.id} data-testid={`client-task-${task.id}`}
                      className={`flex items-center gap-2.5 py-2.5 group ${task.status === "done" ? "opacity-60" : ""}`}>
                      <button onClick={() => toggleTask(task)} className="flex-shrink-0">
                        {task.status === "done"
                          ? <CheckSquare className="w-4 h-4 text-green-500" />
                          : <Square className="w-4 h-4 text-gray-300 hover:text-gray-600" />}
                      </button>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[task.priority] || PRIORITY_DOT.medium}`} />
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm ${task.status === "done" ? "line-through text-gray-400" : "text-gray-800"}`}>{task.title}</span>
                        <span className="text-[10px] text-gray-400 ml-2">{TYPE_LABEL[task.task_type]} · {task.due_date}</span>
                      </div>
                      <button onClick={() => deleteTask(task.id)} className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm flex-shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right column - timeline + quick actions */}
        <div className="space-y-6">
          {/* Quick log */}
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">Log Interaction</h3>
            <div className="flex gap-2 mb-3">
              {["call", "message", "email", "meeting"].map(t => (
                <button key={t} onClick={() => setInteractionType(t)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm capitalize transition-colors ${interactionType === t ? "bg-yellow-400 text-gray-900" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {t}
                </button>
              ))}
            </div>
            <Textarea value={interactionNote} onChange={e => setInteractionNote(e.target.value)} placeholder="What happened?" className="bg-white mb-2" rows={2} />
            <button onClick={addInteraction} className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 px-4 py-2 rounded-sm text-sm">
              <Send className="w-4 h-4" /> Log {interactionType}
            </button>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">Activity Timeline</h3>
            {timeline.length === 0 ? (
              <p className="text-sm text-gray-400">No activity yet</p>
            ) : (
              <div className="space-y-0 max-h-[500px] overflow-y-auto">
                {timeline.map((item, idx) => (
                  <div key={idx} className="flex gap-3 pb-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${TYPE_COLORS[item._subtype] || "bg-gray-400"}`} />
                      {idx < timeline.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-900 capitalize">
                          {item._type === "interaction" ? item.type : item._type === "request" ? "Forklift Request" : item.title || "Appointment"}
                        </span>
                        <span className="text-[10px] text-gray-400">{item._date ? new Date(item._date).toLocaleDateString() : ""}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {item._type === "interaction" && (item.notes || "Logged")}
                        {item._type === "appointment" && `${item.status || "scheduled"} — ${item.notes || ""}`}
                        {item._type === "request" && (item.notes || `${item.condition || "any"} · ${item.engine_type || "any"} · ${item.capacity_min || "—"}-${item.capacity_max || "—"} lbs`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact quick actions */}
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">Quick Contact</h3>
            <div className="space-y-2">
              {c.phone && (
                <a href={`tel:${c.phone}`} className="w-full inline-flex items-center justify-center gap-2 bg-green-50 text-green-800 hover:bg-green-100 border border-green-200 px-4 py-2 rounded-sm text-sm font-medium transition-colors">
                  <Phone className="w-4 h-4" /> Call {c.phone}
                </a>
              )}
              {c.email && c.email.indexOf("@noemail") === -1 && (
                <a href={`mailto:${c.email}`} className="w-full inline-flex items-center justify-center gap-2 bg-blue-50 text-blue-800 hover:bg-blue-100 border border-blue-200 px-4 py-2 rounded-sm text-sm font-medium transition-colors">
                  <Mail className="w-4 h-4" /> Email {c.email}
                </a>
              )}
            </div>
          </div>

          {/* Deals — Machines this client is related to */}
          <DealsPanel mode="client" clientId={clientId} />
        </div>
      </div>
    </div>
  );
}
